import { communicationService } from '@ad-engine/communication';
import type { AdSlot } from '@ad-engine/core';
import { context, utils } from '@ad-engine/core';
import { Injectable } from '@wikia/dependency-injection';
import { AD_ENGINE_AD_RESIZED } from '../../communication/events/events-ad-engine-slot';
import { DataWarehouseTracker } from '../data-warehouse';
import { trackingUrls } from '../tracking-urls';

export interface AdSize {
	width: number;
	height: number;
}

/**
 * Wrapper for ad size tracking
 */
@Injectable()
export class AdSizeTracker {
	logGroup = 'ad-size-tracker';

	constructor(private dwTracker: DataWarehouseTracker) {}

	init(): void {
		communicationService.on(
			AD_ENGINE_AD_RESIZED,
			({ slot }) => {
				this.track(slot);
			},
			false,
		);
	}

	getFirstChild(elem: HTMLElement): HTMLElement {
		return elem.firstChild as HTMLElement;
	}

	getAdSize(position: string): AdSize {
		let height, width;
		try {
			const elemIframe = (
				document.querySelector(
					`#${position} [id*='google_ads_iframe_\\/5441'] iframe`,
				) as HTMLIFrameElement
			)?.contentWindow?.document?.querySelector('html');
			height = elemIframe?.offsetHeight;
			width = elemIframe?.offsetWidth;
			if (!height || !width) {
				let elem: HTMLElement = document.querySelector(
					`#${position} [id*='google_ads_iframe_\\/5441'] div`,
				) as HTMLDivElement;
				while (!height || !width) {
					elem = this.getFirstChild(elem);
					height = elem.clientHeight;
					width = elem.clientWidth;
				}
			}
		} catch (_) {
			width = null;
			height = null;
		}
		return {
			height,
			width,
		};
	}

	getRealAdSize(slot: AdSlot, position: string): AdSize {
		// Most of the time iFrame should be enough
		let adSizes = slot.getIframeSize();
		utils.logger(this.logGroup, `iframe size: ${adSizes.width}x${adSizes.height}`);

		if (!adSizes.width || !adSizes.height) {
			// In some cases, when ad is rendered outside the iFrame, it's better to use container
			adSizes = this.getAdSize(position);
			utils.logger(this.logGroup, `element size: ${adSizes.width}x${adSizes.height}`);
		}

		if (!adSizes.width || !adSizes.height) {
			// slot.getElement() is the last resort if both - container and iFrame - do not show a proper size.
			adSizes = {
				width: slot.getElement()?.offsetWidth || 0,
				height: slot.getElement()?.offsetHeight || 0,
			};
		}
		return adSizes;
	}

	track(slot: AdSlot): void {
		setTimeout(() => {
			utils.logger(this.logGroup, 'Sending tracking event for ', slot.getMainPositionName().trim());
			const position = slot.getMainPositionName();
			try {
				const adSize = this.getRealAdSize(slot, position);
				const adSizeObject = {
					slot_size: slot.getCreativeSize() || '',
					real_ad_size: `${adSize.width}x${adSize.height}`,
					requested_sizes: slot.config.defaultSizes.map((sizePair) => sizePair.join('x')).join(','),
					advertiser_id: slot.advertiserId,
					order_id: slot.orderId,
					line_item_id: slot.lineItemId || '',
					creative_id: slot.creativeId || '',
					slot_id: slot.getUid() || '',
					position: position,
					country_code: context.get('geo.country'),
					region: context.get('geo.region'),
					rv: slot.getTargetingProperty('rv') || '',
				};
				this.dwTracker.track(adSizeObject, trackingUrls.AD_ENG_AD_SIZE_INFO);
			} catch (e) {
				utils.logger(this.logGroup, 'Error when reading a data from: ' + position, e);
			}
		}, 0);
	}
}
